//pages/Landing.tsx
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  BarChart3,
  Signal,
  DollarSign,
  Shield,
  Clock,
  Infinity,
  TrendingUp,
  Zap,
  Brain,
  Target,
  Database,
  Cog,
} from "lucide-react";
import Layout from "@/components/Layout";
import { useMemo } from "react";
// SEO: Import reusable SEO component to inject page-specific meta tags & JSON-LD.
import SEO from '@/components/SEO';
import { useAuth } from "@/hooks/useAuth";
import { usePaymentPackages } from "@/hooks/usePayments";
import { ShimmerBlock } from "@/components/ui/shimmer";
import HeroAnimation from "@/components/HeroAnimation";

const isTrialLike = (name: unknown) => /\btrial\b/i.test(String(name || ''));

export default function Landing() {
  const { user, isAuthenticated, userStatsQuery } = useAuth();
  const packagesQuery = usePaymentPackages();

  const stats = (userStatsQuery?.data as any) || {};
  const activeSignals =
    typeof stats.activeSignals === "number" ? stats.activeSignals : 0;
  const avgConfidence =
    typeof stats.avgConfidence === "number" ? Math.round(stats.avgConfidence) : 0;
  const credits =
    (user as any)?.credits ?? stats.credits ?? stats.creditsRemaining ?? 0;

  const primaryCta = isAuthenticated
    ? { href: "/dashboard", label: "Open Dashboard" }
    : { href: "/register", label: "Start for Free" };
  const secondaryCta = isAuthenticated
    ? { href: "/start-trading", label: "Start Analysis" }
    : { href: "/login", label: "Sign In" };

  type LandingPlan = {
    name: string;
    price: string; // display price with $
    credits: string; // e.g. "3 Tokens (3 Analysis Outputs)"
    highlight: boolean;
    popular: boolean;
    save?: string; // discount label
    badge?: string; // BEST VALUE / POPULAR etc
    unit?: string; // per report pricing
    ctaLabel?: string; // override button text
    qty?: number; // numeric quantity for reports/tokens
    tokensText?: string; // e.g., "3 Premium Research Tokens"
    reportsText?: string; // e.g., "3 AI-Driven Market Insight Reports"
    features?: string[]; // optional plan-specific bullet list
  };
  // Backend plans mapping (like Pricing page)
  const apiPlans: LandingPlan[] = useMemo(() => {
    const list = packagesQuery.data || [];
    const filtered = list
      .filter((p: any) => p.is_active !== false)
      .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    const paidPkgs = filtered
      .map((p: any) => ({ pkg: p, credits: Number(p.credits || 0), amount: Number(p.amount || 0) }))
      .filter(x => x.credits > 0 && Number.isFinite(x.amount) && x.amount > 0);
    const trialPkg = paidPkgs.find(x => isTrialLike(x.pkg?.name) || isTrialLike(x.pkg?.package_name) || isTrialLike(x.pkg?.package_code));
    const fallbackBase = paidPkgs.slice().sort((a, b) => (b.amount / b.credits) - (a.amount / a.credits))[0];
    const base = trialPkg || fallbackBase;
    const baseUnit = base ? (base.amount / base.credits) : null;

    const maxCredits = filtered.reduce((m: number, p: any) => Math.max(m, Number(p.credits || 0)), 0);
    return filtered.map((p: any) => {
      const isHighlight = Number(p.credits || 0) === maxCredits || /ultimate|pro/i.test(p.name || "");
      const qty = Number(p.credits || 0);
      const unit = qty ? `$${(Number(p.amount) / qty).toFixed(2)} / per report` : undefined;

      const amount = Number(p.amount || 0);
      const planUnit = qty > 0 ? (amount / qty) : null;
      const isTrial = isTrialLike(p.name) || isTrialLike(p.package_code);
      const save = (!isTrial && baseUnit != null && planUnit != null && Number.isFinite(planUnit) && planUnit < baseUnit)
        ? (() => {
            const comparative = baseUnit * qty;
            const s = +(comparative - amount).toFixed(2);
            if (!(s > 0)) return undefined;
            return `Save $${s.toFixed(2)}`;
          })()
        : undefined;

      return {
        name: p.name,
        price: `$${Number(p.amount).toFixed(2)}`,
        credits: `${p.credits} Premium Research ${Number(p.credits) === 1 ? 'Token' : 'Tokens'}`,
        highlight: !!isHighlight,
        popular: false,
        badge: isHighlight ? "BEST VALUE" : undefined,
        unit,
        qty,
        tokensText: `${qty} Premium Research ${qty === 1 ? 'Token' : 'Tokens'}`,
        reportsText: `${qty} AI-Driven Market Insight ${qty === 1 ? 'Report' : 'Reports'}`,
        save,
      } as LandingPlan;
    });
  }, [packagesQuery.data]);

  // Include FREE Test plus backend plans for guests; authenticated users see only backend plans
  const freeTest: LandingPlan = {
    name: "FREE Test",
    price: "$0.00",
    credits: "1 AI Data Token (Standard Report)",
    unit: "$0.00 / per report",
    highlight: false,
    popular: false,
    ctaLabel: "Start for Free",
    qty: 1,
    tokensText: "1 AI Data Token (Standard Report)",
    reportsText: "1 AI-Driven Market Insight Report",
    features: [
      "Real-time Data Computation",
      "Algorithmic Risk Parameters Included",
      "Free token is non-refundable in case of NO ACTION result",
      "Intended for first-time testing only",
    ],
  };
  const plans: LandingPlan[] = isAuthenticated ? apiPlans : [freeTest, ...apiPlans];
  const lgCols = isAuthenticated ? 'lg:grid-cols-5' : 'lg:grid-cols-6';

  // Build OfferCatalog JSON-LD (lightweight) from plans for richer pricing snippet without conflicting with /pricing.
  const offerCatalog = plans.length ? {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    'name': 'AI Market Analysis Credit Packs',
    'identifier': 'landing-offers',
    'itemListElement': plans.slice(0,6).map(p => ({
      '@type': 'Offer',
      'name': p.name,
      'price': p.price.replace(/[$]/,'') ,
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock'
    }))
  } : null;

  return (
    <Layout>
      {/* SEO HEAD TAGS */}
      <SEO
        title="AI Market Research & Analysis Platform | TradeSagePro"
        description="TradeSagePro (tradesagepro) delivers high-confidence AI market research insights with data points for position initiation, risk management parameters, and target data points for position closure — plus research-driven context. Start free to explore data-driven analysis."
        canonical="https://tradesagepro.com/"
        type="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            'name': 'TradeSagePro',
            'alternateName': ['tradesagepro', 'Trade Sage Pro'],
            'url': 'https://tradesagepro.com/',
            'description': 'AI-powered market research and data analysis platform providing actionable analysis insights and real-time context.',
            'publisher': {
              '@type': 'Organization',
              'name': 'TradeSagePro',
              'logo': {
                '@type': 'ImageObject',
                'url': 'https://tradesagepro.com/favicon.svg'
              }
            }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            'name': 'Primary pages',
            'itemListElement': [
              {
                '@type': 'SiteNavigationElement',
                'position': 1,
                'name': 'Home',
                'url': 'https://tradesagepro.com/'
              },
              {
                '@type': 'SiteNavigationElement',
                'position': 2,
                'name': 'How It Works',
                'url': 'https://tradesagepro.com/how-it-works'
              },
              {
                '@type': 'SiteNavigationElement',
                'position': 3,
                'name': 'About',
                'url': 'https://tradesagepro.com/about'
              }
            ]
          },
          offerCatalog
        ].filter(Boolean) as any}
      />
      {/* END SEO TAGS - rationale: homepage needs WebSite schema + product teaser offers for enhanced visibility */}
    <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] overflow-x-hidden">
      {/* Inline styles for animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
  {/* Header is provided by Layout */}

    {/* Hero */}
    <section className="relative overflow-hidden py-20 lg:py-32 bg-[hsl(226,26%,7%)]" role="banner">
        {/* ფონური ანიმაციები */}
        <div className="absolute inset-0 opacity-20 bg-[hsl(226,26%,7%)]">
          <div className="grid grid-cols-12 gap-4 h-full">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="border-r border-[hsl(193,100%,50%)] animate-pulse"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
          <div className="absolute top-10 left-10 w-2 h-2 bg-[hsl(193,100%,50%)] rounded-full animate-bounce opacity-70" />
          <div className="absolute top-32 right-20 w-1 h-1 bg-[hsl(193,100%,50%)] rounded-full animate-ping opacity-60" />
          <div className="absolute bottom-20 left-32 w-3 h-3 bg-[hsl(193,100%,50%)] rounded-full animate-pulse opacity-50" />
          <div className="absolute top-20 right-1/4 w-32 h-32 bg-[hsl(193,100%,50%)]/10 rounded-full blur-xl animate-pulse" />
          <div
            className="absolute bottom-32 left-1/4 w-24 h-24 bg-[hsl(193,100%,50%)]/15 rounded-full blur-2xl animate-pulse"
            style={{ animationDelay: "1s" }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in" aria-labelledby="hero-heading">
              <div className="flex items-center space-x-2 mb-6">
                <span className="bg-[hsl(193,100%,50%)]/20 text-[hsl(193,100%,50%)] px-3 py-1 rounded-full text-sm font-semibold border border-[hsl(193,100%,50%)]/30">
                  🚀 AI-Powered Market Research
                </span>
              </div>
              <h1 id="hero-heading" className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                AI-Powered{" "}
                <span className="text-[hsl(193,100%,50%)] bg-gradient-to-r from-[hsl(193,100%,50%)] to-[hsl(193,100%,70%)] bg-clip-text text-transparent">
                  Market Analysis Insights
                </span>{" "}
                For Smarter Decisions
              </h1>
              <p className="text-xl text-[hsl(210,9%,91%)] mb-8 leading-relaxed">
                TradeSagePro (tradesagepro) provides AI-generated research reports with data points for position initiation, risk management parameters, and target data points for position closure.
              </p>

              {/* დინამიკური ბეჯები stats-იდან */}
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-[hsl(142,71%,45%)]" />
                  <span className="text-sm text-white">
                    {avgConfidence > 0
                      ? `${avgConfidence}% Model Metric (Concordance)`
                      : "High-Confidence Analysis Insights"}
                  </span>
                </div>
                <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg">
                  <Zap className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                  <span className="text-sm text-white">
                    {activeSignals > 0
                      ? `${activeSignals} Active Analyses`
                      : "Real-time Analyses"}
                  </span>
                </div>
                <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg">
                  <Shield className="w-4 h-4 text-[hsl(142,71%,45%)]" />
                  <span className="text-sm text-white">Risk Control Metrics</span>
                </div>
              </div>

              {/* CTA-ები */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={primaryCta.href}>
                  <Button className="bg-gradient-to-r from-[hsl(193,100%,50%)] to-[hsl(193,100%,60%)] text-[hsl(226,26%,7%)] px-8 py-4 text-lg font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-[hsl(193,100%,50%)]/25">
                    <Signal className="w-5 h-5 mr-2" />
                    {primaryCta.label}
                  </Button>
                </Link>
                <Link href={secondaryCta.href}>
                  <Button
                    variant="outline"
                    className="border-[hsl(193,100%,50%)] text-[hsl(193,100%,50%)] hover:bg-[hsl(193,100%,50%)] hover:text-[hsl(226,26%,7%)] px-8 py-4 text-lg transition-all duration-300"
                  >
                    {secondaryCta.label}
                  </Button>
                </Link>
              </div>

              {/* Tokens chip for authenticated users */}
              <div className="mt-8 flex items-center flex-wrap gap-6 text-sm text-[hsl(210,9%,80%)]">
                {isAuthenticated && (
                  <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-3 py-1 rounded-lg">
                    <DollarSign className="w-4 h-4 text-[hsl(142,71%,45%)]" />
                    <span>{credits} Tokens</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right column: hero GIF */}
            <div className="hidden lg:flex items-center justify-center">
              <HeroAnimation className="max-h-80" ariaLabel="AI analysis visual" />
            </div>
          </div>
        </div>
      </section>

  {/* What is TradeSagePro */}
      <section className="py-20 bg-gradient-to-b from-[hsl(226,26%,7%)] to-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=70"
                  alt="Professional trading setup with multiple monitors displaying market data"
                  width={800}
                  height={600}
                  loading="lazy"
                  decoding="async"
                  // Use lowercase fetchpriority to avoid React warning
                  // @ts-expect-error non-standard attribute supported by browsers
                  fetchpriority="low"
                  className="rounded-2xl shadow-2xl w-full h-auto hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(193,100%,50%)]/20 to-transparent rounded-2xl" />
              </div>
            </div>
            <div className="order-1 lg:order-2 animate-slide-in">
              <div className="flex items-center space-x-3 mb-6">
                <BarChart3 className="text-[hsl(193,100%,50%)] w-8 h-8" />
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  What is TradeSagePro?
                </h2>
              </div>
              <p className="text-lg text-[hsl(210,9%,91%)] mb-6 leading-relaxed">
                TradeSagePro is an AI-powered market analysis service that studies
                market conditions and generates high-potential data insights.
              </p>

              {/* ✅ აშკარა JSX გამოყენება — აღარ გვჭირდება დინამიკური <step.icon /> */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3 p-4 bg-[hsl(215,15%,18%)] rounded-lg hover:bg-[hsl(215,15%,20%)] transition-colors duration-300">
                  <div className="w-6 h-6 bg-[hsl(142,71%,45%)] rounded-full flex items-center justify-center mt-1 shadow-lg">
                    <Database className="text-white w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Data Collection</h3>
                    <p className="text-[hsl(210,9%,91%)]">
                      Advanced data ingestion from market, news and social sources
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-[hsl(215,15%,18%)] rounded-lg hover:bg-[hsl(215,15%,20%)] transition-colors duration-300">
                  <div className="w-6 h-6 bg-[hsl(142,71%,45%)] rounded-full flex items-center justify-center mt-1 shadow-lg">
                    <Brain className="text-white w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">AI Processing</h3>
                    <p className="text-[hsl(210,9%,91%)]">
                      Pattern mining with modern AI models in real time
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-[hsl(215,15%,18%)] rounded-lg hover:bg-[hsl(215,15%,20%)] transition-colors duration-300">
                  <div className="w-6 h-6 bg-[hsl(142,71%,45%)] rounded-full flex items-center justify-center mt-1 shadow-lg">
                    <Target className="text-white w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Analysis Delivery</h3>
                    <p className="text-[hsl(210,9%,91%)]">
                      Suggested entries, Risk/Exit Levels and confidence scores
                    </p>
                  </div>
                </div>
              </div>

              <Link href="/about">
                <Button
                  variant="outline"
                  className="border-[hsl(193,100%,50%)] text-[hsl(193,100%,50%)] hover:bg-[hsl(193,100%,50%)] hover:text-[hsl(226,26%,7%)] hover:scale-105 transition-all duration-300"
                >
                  Learn More →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works flow */}
      <section className="py-20 bg-[hsl(226,26%,7%)] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              How TradeSagePro Works
            </h2>
            <p className="text-xl text-[hsl(210,9%,91%)] max-w-2xl mx-auto">
              Our advanced AI system follows a precise 3-step process to
              generate validated market analysis outputs
            </p>
          </div>

          {/* Loading indicator while fetching pricing */}
          {packagesQuery.isLoading && (
            <div className="mb-6" role="status" aria-live="polite" aria-label="Loading pricing">
              <div className="h-2 w-full bg-[#1C2A44] rounded overflow-hidden">
                <div className="h-full w-1/3 bg-[hsl(193,100%,50%)] animate-pulse rounded" />
              </div>
              <div className="mt-2 text-xs text-gray-400">Loading pricing…</div>
            </div>
          )}

          {/* პროცესის ვიზუალური რიგი — ყველა აიკონი აშკარად გამოიყენება */}
          <div className="mt-6 flex justify-center overflow-x-auto">
            <div className="flex items-center space-x-4 pb-4 min-w-max px-4">
              <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg whitespace-nowrap hover:bg-[hsl(215,15%,20%)] transition-colors duration-300 border border-transparent hover:border-[hsl(193,100%,50%)]/30">
                <BarChart3 className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                <span className="text-white text-sm">Market Data</span>
              </div>
              <span className="text-[hsl(193,100%,50%)] animate-pulse">→</span>
              <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg whitespace-nowrap hover:bg-[hsl(215,15%,20%)] transition-colors duration-300 border border-transparent hover:border-[hsl(193,100%,50%)]/30">
                <Cog className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                <span className="text-white text-sm">AI Analysis</span>
              </div>
              <span className="text-[hsl(193,100%,50%)] animate-pulse">→</span>
              <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg whitespace-nowrap hover:bg-[hsl(215,15%,20%)] transition-colors duration-300 border border-transparent hover:border-[hsl(193,100%,50%)]/30">
                <Signal className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                <span className="text-white text-sm">Validated Analysis Output</span>
              </div>
              <span className="text-[hsl(193,100%,50%)] animate-pulse">→</span>
              <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg whitespace-nowrap hover:bg-[hsl(215,15%,20%)] transition-colors duration-300 border border-transparent hover:border-[hsl(193,100%,50%)]/30">
                <DollarSign className="w-4 h-4 text-[hsl(142,71%,45%)]" />
                <span className="text-white text-sm">Market Performance Metrics</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gradient-to-b from-[hsl(226,26%,7%)] to-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Choose Your Analysis Plan
            </h2>
            <p className="text-xl text-[hsl(210,9%,91%)] max-w-2xl mx-auto">
              Flexible credit-based pricing with no subscriptions. Pay only for
              the analyses you run.
            </p>
            {/* Added primary pricing CTA directing to login (or dashboard if authenticated) */}
            <div className="mt-8">
              <Link href={isAuthenticated ? "/dashboard" : "/login"}>
                <Button className="bg-gradient-to-r from-[hsl(193,100%,50%)] to-[hsl(193,100%,60%)] text-[hsl(226,26%,7%)] px-8 py-4 text-lg font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-[hsl(193,100%,50%)]/25">
                  Get Started Now
                </Button>
              </Link>
            </div>
          </div>

          <div className={`grid md:grid-cols-3 ${lgCols} gap-6`}>
            {packagesQuery.isLoading && (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={`sk-${i}`} className="bg-[hsl(215,15%,18%)] border-gray-700">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <ShimmerBlock className="h-6 w-40" />
                        <div className="flex items-end gap-2">
                          <ShimmerBlock className="h-8 w-20" />
                          <ShimmerBlock className="h-4 w-10" />
                        </div>
                        <ShimmerBlock className="h-4 w-48" />
                        <ShimmerBlock className="h-4 w-32" />
                        <ShimmerBlock className="h-10 w-full" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
            {!packagesQuery.isLoading && plans.map((plan, i) => (
              <Card
                key={i}
                className={`bg-[hsl(215,15%,18%)] ${
                  plan.highlight
                    ? "border-2 border-[hsl(193,100%,50%)] shadow-lg shadow-[hsl(193,100%,50%)]/25 scale-105"
                    : plan.popular
                    ? "border-2 border-[hsl(142,71%,45%)]"
                    : "border-gray-700"
                } hover:border-[hsl(193,100%,50%)] hover:scale-105 transition-all duration-300 relative`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span
                      className={`${
                        plan.badge === "BEST VALUE"
                          ? "bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)]"
                          : "bg-[hsl(142,71%,45%)] text-white"
                      } px-3 py-1 rounded-full text-xs font-semibold shadow-lg`}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[hsl(142,71%,45%)] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                      POPULAR
                    </span>
                  </div>
                )}
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-[hsl(193,100%,50%)]">
                      {plan.price.split(".")[0]}
                    </span>
                    <span className="text-[hsl(210,9%,91%)]">
                      .{plan.price.split(".")[1] || '00'}
                    </span>
                  </div>
                  <div className="text-white mb-0.5 font-semibold text-sm">
                    {plan.tokensText || plan.credits}
                  </div>
                  {plan.reportsText && (
                    <div className="text-white/85 mb-2 text-xs">
                      {plan.reportsText}
                    </div>
                  )}
                  {plan.unit && (
                    <div className="text-xs text-[hsl(210,9%,80%)] mb-2 font-mono">{plan.unit}</div>
                  )}
                  {/* Plan-specific bullets (FREE Test) with fallback supportive lines */}
                  {plan.features?.length ? (
                    <div className="text-[hsl(210,9%,80%)] text-xs space-y-1 mb-4 text-left">
                      {plan.features.map((f, idx) => (
                        <div key={idx}>• {f}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[hsl(210,9%,80%)] text-xs space-y-1 mb-4">
                      <div>Real-time Data Computation</div>
                      <div>Algorithmic Risk Parameters Included</div>
                    </div>
                  )}
                  {plan.save && (
                    <div className="text-[hsl(142,71%,45%)] text-xs mb-4 font-semibold">
                      {plan.save}
                    </div>
                  )}
                  <Link href={isAuthenticated ? "/dashboard" : (i === 0 ? "/register" : "/login") }>
                    <Button
                      className={
                        plan.highlight
                          ? "w-full bg-gradient-to-r from-[hsl(193,100%,50%)] to-[hsl(193,100%,60%)] text-[hsl(226,26%,7%)] hover:scale-105 transition-all duration-300 shadow-lg"
                          : "w-full border border-[hsl(193,100%,50%)] text-[hsl(193,100%,50%)] bg-transparent hover:bg-[hsl(193,100%,50%)] hover:text-[hsl(226,26%,7%)] transition-all duration-300"
                      }
                    >
                      {isAuthenticated
                        ? "Go to Dashboard"
                        : plan.ctaLabel
                        ? plan.ctaLabel
                        : plan.highlight
                        ? "Best Deal"
                        : plan.popular
                        ? "Popular"
                        : "Choose Plan"}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-[hsl(210,9%,91%)] mb-6 text-lg">
              All plans include full access to AI analysis outputs with no expiration.
              Tokens never expire.
            </p>
            <div className="flex justify-center items-center space-x-8 text-sm text-[hsl(210,9%,91%)]">
              <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg">
                <Shield className="text-[hsl(142,71%,45%)] w-4 h-4" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg">
                <Clock className="text-[hsl(142,71%,45%)] w-4 h-4" />
                <span>Instant Access</span>
              </div>
              <div className="flex items-center space-x-2 bg-[hsl(215,15%,18%)] px-4 py-2 rounded-lg">
                <Infinity className="text-[hsl(142,71%,45%)] w-4 h-4" />
                <span>No Expiration</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      

    </div>
    </Layout>
  );
}
